/**
 * Full-page Share Case — replaces the CaseDetail.js "Share Case" dialog.
 * Route: /cases/:caseId/share
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Share2, Loader2, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

/**
 * Iter123 — Company Case Sharing Fix: shows which companies/users currently
 * have access to this case, with a Revoke action. Uses existing endpoints
 * GET /api/cases/:id/shares and DELETE /api/cases/:id/shares/:share_id.
 */
function ExistingSharesPanel({ caseId, token }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/shares`, { headers: { Authorization: `Bearer ${token}` } });
      setShares(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [caseId, token]); // eslint-disable-line

  const revoke = async (share) => {
    if (!window.confirm(`Revoke access for "${share.target_company_name}"?`)) return;
    setBusyId(share.id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/shares/${share.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return toast({ title: 'Revoke failed', variant: 'destructive' });
      toast({ title: 'Access revoked' });
      await load();
    } finally { setBusyId(null); }
  };

  return (
    <div className="border border-slate-700 rounded-md p-3 bg-slate-900/40" data-testid="existing-shares-panel">
      <div className="text-xs font-semibold text-slate-300 mb-2">Companies with current access ({shares.length})</div>
      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : shares.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Not shared yet.</div>
      ) : (
        <div className="space-y-1">
          {shares.map(s => (
            <div key={s.id} className="flex items-center justify-between gap-2 bg-slate-900/60 border border-slate-800 rounded px-2 py-1.5" data-testid={`share-row-${s.id}`}>
              <div className="min-w-0">
                <div className="text-xs text-white truncate">{s.target_company_name || s.target_company_id}</div>
                <div className="text-[10px] text-slate-400 font-mono">
                  {(s.permission || 'read').replace(/_/g, ' ').toUpperCase()}
                  {s.target_user_ids?.length ? ` · ${s.target_user_ids.length} user(s)` : ''}
                  {s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              <button onClick={() => revoke(s)} disabled={busyId === s.id}
                className="text-red-400 hover:text-red-300 text-[10px] flex items-center gap-1 disabled:opacity-50"
                data-testid={`share-revoke-${s.id}`}>
                <Trash2 className="w-3 h-3" /> {busyId === s.id ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ShareCasePage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);

  const [caseInfo, setCaseInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [targetUsersLoading, setTargetUsersLoading] = useState(false);
  const [form, setForm] = useState({
    target_company_id: '', target_user_ids: [], permission: 'full_access',
    notes: '', notify_admins: true, notify_users: true,
  });

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCaseInfo).catch(() => {});
    fetch(`${BACKEND_URL}/api/sharing/target-companies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setAllCompanies).catch(() => setAllCompanies([]));
  }, [caseId, token]);

  const loadCompanyUsers = async (companyId) => {
    if (!companyId) { setTargetUsers([]); return; }
    setTargetUsersLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sharing/target-companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTargetUsers(res.ok ? await res.json() : []);
    } catch { setTargetUsers([]); }
    finally { setTargetUsersLoading(false); }
  };

  const goBack = () => navigate(`/cases/${caseId}`);

  const submit = async () => {
    if (!form.target_company_id) {
      toast({ title: 'Missing company', description: 'Select a target company', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Share failed (${res.status})`);
      const data = await res.json().catch(() => ({}));
      toast({
        title: 'Case shared',
        description: data.notified_users != null ? `${data.notified_users} user(s) notified.` : 'Case shared successfully',
      });
      goBack();
    } catch (e) {
      toast({ title: 'Could not share case', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="share-case-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="share-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Share2 className="w-6 h-6 text-amber-300" /> Share Case with Another Company
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-3xl">
        {/* Iter123 — existing shares list + revoke */}
        <ExistingSharesPanel caseId={caseId} token={token} />

        <div>
          <Label className="text-slate-300">Target Company *</Label>
          <Select
            value={form.target_company_id}
            onValueChange={v => { setForm(f => ({ ...f, target_company_id: v, target_user_ids: [] })); loadCompanyUsers(v); }}
          >
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="share-company-select">
              <SelectValue placeholder="Select company..." />
            </SelectTrigger>
            <SelectContent>
              {allCompanies.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500 italic">No target companies available.</div>
              ) : allCompanies.map(c => (
                <SelectItem key={c.id} value={c.id} data-testid={`share-company-opt-${c.id}`}>
                  {c.name}{c.type && <span className="text-slate-400 text-[10px] ml-1.5">· {c.type}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-1">
            {allCompanies.length} company{allCompanies.length === 1 ? '' : 'ies'} available for sharing
          </p>
        </div>

        {form.target_company_id && (
          <div>
            <Label className="text-slate-300">Assign to Specific User(s)</Label>
            <p className="text-[10px] text-slate-500 mb-1.5">
              Leave empty to share with the entire company. Selected users get their own notification and access.
            </p>
            {targetUsersLoading ? (
              <p className="text-xs text-slate-400 py-2"><Loader2 className="inline w-3 h-3 mr-1 animate-spin" /> Loading users…</p>
            ) : targetUsers.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No active users in this company.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto border border-slate-700 rounded-md p-2 space-y-1 bg-slate-900/40" data-testid="share-user-picker">
                {targetUsers.map(u => (
                  <label key={u.id} className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer hover:bg-slate-800 rounded px-1.5 py-1" data-testid={`share-user-opt-${u.id}`}>
                    <input type="checkbox" className="rounded border-slate-600"
                      checked={form.target_user_ids.includes(u.id)}
                      onChange={e => setForm(f => ({
                        ...f,
                        target_user_ids: e.target.checked
                          ? [...f.target_user_ids, u.id]
                          : f.target_user_ids.filter(x => x !== u.id),
                      }))}
                    />
                    <span className="flex-1">{u.name || u.username}</span>
                    <span className="text-[10px] text-slate-400 font-mono uppercase">{(u.role || '').replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <Label className="text-slate-300">Permission Level</Label>
          <Select value={form.permission} onValueChange={v => setForm(f => ({ ...f, permission: v }))}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="share-permission-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              <SelectItem value="read_write">Read &amp; Write (basic)</SelectItem>
              <SelectItem value="full_access">Full Access — Reports, Notes, Evidence, ATAs, Subjects, Logs</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border border-slate-700 rounded-md p-3 space-y-2 bg-slate-900/40">
          <div className="text-xs font-semibold text-slate-300">Notifications</div>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input type="checkbox" className="rounded border-slate-600"
              checked={form.notify_admins}
              onChange={e => setForm(f => ({ ...f, notify_admins: e.target.checked }))}
              data-testid="share-notify-admins" />
            Notify company admins (email + in-app)
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input type="checkbox" className="rounded border-slate-600"
              checked={form.notify_users}
              onChange={e => setForm(f => ({ ...f, notify_users: e.target.checked }))}
              data-testid="share-notify-users" />
            Notify selected users {form.target_user_ids.length > 0 && <span className="text-slate-400">({form.target_user_ids.length} selected)</span>}
          </label>
        </div>

        <div>
          <Label className="text-slate-300">Notes (Optional)</Label>
          <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white" rows={3} placeholder="Reason for sharing..." />
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="share-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="share-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving || !form.target_company_id}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="confirm-share-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sharing…</> : 'Share Case'}
        </Button>
      </div>
    </div>
  );
}
